import { orderBy } from 'firebase/firestore';
import { useRouter } from 'next/router';
import { values } from 'ramda';
import { useMemo } from 'react';
import { Suppliers, Weddings } from '@bridebook/models';
import {
  ISupplier as BBSupplier,
  ISupplier,
} from '@bridebook/models/source/models/Suppliers.types';
import type { FetchEnrichedSupplierParams } from '@bridebook/toolbox/src/supplier/enrich/types';
import { decodePublicSupplierId, extractPublicId } from '@bridebook/toolbox/src/hashids';
import { toMillis } from '@bridebook/toolbox/src/time-utils/time-utils';
import { useQuery } from '@tanstack/react-query';
import type { TRefSupplier } from '@bridebook/web/app-shared/lib/supplier/types';
import { fetchSupplierPromise } from '@bridebook/web/app-shared/lib/supplier/utils/fetch-supplier-promise';
import { getUserL10n } from '@bridebook/web/lib/i18n/selectors';
import { getReflinkParams } from '@bridebook/web/lib/reflinks';
import { getSearchFields } from '@bridebook/web/lib/search/selectors';
import { fetchSupplierData } from '@bridebook/web/lib/supplier/actions';
import { SearchResponseFields } from '@bridebook/web/lib/types';
import { UrlHelper } from '@bridebook/web/lib/url-helper';
import { useSelector } from '@bridebook/web/lib/utils';
import { ISupplier as CustomSupplier } from '@bridebook/models/source/models/Weddings/Suppliers.types';
import { getWeddingId } from '@bridebook/web/lib/weddings/selectors';
import { isPromiseFulfilled } from '@bridebook/toolbox/src/is';

export const SUPPLIER_QUERY_CACHE_TIME = toMillis(15, 'min');

export interface ISupplierQueryParams {
  supplierQueryId: string;
  slug: string;
  refSupplier: TRefSupplier;
  supplierId: ISupplier['id'];
}

export type BBOrCustomSupplier = BBSupplier | CustomSupplier;

export function isCustomSupplier(supplier: BBOrCustomSupplier): supplier is CustomSupplier {
  return !!supplier && 'customSupplier' in supplier;
}

export const supplierQueryKey = (supplierId?: string) => ['supplier', supplierId || null];

/** A supplier by id, from the directory or the couple's own custom ones. */
export const fetchSupplier = async (
  supplierId: string | undefined,
  weddingId: string,
): Promise<BBOrCustomSupplier | null> => {
  if (!supplierId) return null;

  const supplier = await Suppliers._.getById(supplierId).get();
  if (!supplier) {
    return await Weddings._.getById(weddingId).Suppliers.getById(supplierId).get();
  }
  return supplier;
};

export const useSupplier = (params: { supplierId?: string }) => {
  const weddingId = useSelector(getWeddingId);

  const { data, isLoading, fetchStatus } = useQuery<BBOrCustomSupplier | null>(
    supplierQueryKey(params.supplierId),
    () => fetchSupplier(params.supplierId, weddingId),
    {
      refetchOnWindowFocus: false,
    },
  );

  return {
    data,
    isLoading: isLoading && fetchStatus !== 'idle',
    staleTime: SUPPLIER_QUERY_CACHE_TIME,
    cacheTime: SUPPLIER_QUERY_CACHE_TIME,
    enabled: !!params.supplierId,
  };
};

// Shared with thunks, so they read the same cache entry as the hook.
export const getSupplierPricingGroupedByYearQuery = (supplierId?: string) => ({
  queryKey: ['supplier-pricing-count', { supplierId }],
  queryFn: async () => {
    if (!supplierId) return null;

    return await Suppliers._.getById(supplierId).Prices.getAll();
  },
  cacheTime: SUPPLIER_QUERY_CACHE_TIME,
  staleTime: SUPPLIER_QUERY_CACHE_TIME,
});

export const useSupplierPricingGroupedByYear = (
  params: {
    supplierId?: string;
  },
  settings?: { disable?: boolean },
) => {
  const { data, isLoading } = useQuery({
    ...getSupplierPricingGroupedByYearQuery(params.supplierId),
    enabled: !settings?.disable && !!params.supplierId,
    refetchOnWindowFocus: false,
  });

  const allowedYears = useMemo(() => Object.keys(data ?? {}), [data]);

  return {
    data,
    isLoading,
    allowedYears,
  };
};

export const useSupplierPricing = ({
  supplierId,
  year,
  guests,
  packageId,
  optionIds,
}: {
  supplierId?: string;
  year?: number;
  guests?: number;
  /** Package the selected options belong to. */
  packageId?: string;
  /** Options folded into that package's estimate. */
  optionIds?: string[];
}) => {
  const isEnabled = !!supplierId && !!year && typeof guests !== 'undefined';

  // Sorted so picking the same options in a different order doesn't refetch.
  const options = useMemo(() => [...(optionIds ?? [])].sort(), [optionIds]);

  /**
   * Every package of the year priced for the guest count. Nothing here depends on which
   * package the couple is looking at, so the list survives them browsing through them.
   */
  const { data, isLoading, isFetching, isPreviousData } = useQuery(
    ['supplier-pricing-estimate', supplierId, year, guests],
    async () => {
      if (!supplierId || !year || typeof guests === 'undefined') return null;

      const dataByYear = await Suppliers._.getById(supplierId).Prices.getAll();
      const yearData = dataByYear[year];

      if (!yearData) return [];

      const estimates = await Promise.allSettled(
        yearData.map((yearItem) =>
          Suppliers._.getById(supplierId)
            .Prices.getById(yearItem.id)
            .getEstimate({ day: guests }, yearItem),
        ),
      );

      return estimates.filter(isPromiseFulfilled).map((result) => result.value);
    },
    {
      cacheTime: SUPPLIER_QUERY_CACHE_TIME,
      staleTime: SUPPLIER_QUERY_CACHE_TIME,
      refetchOnWindowFocus: false,
      keepPreviousData: true,
      enabled: isEnabled,
    },
  );

  /**
   * The selected package on its own, with the options picked for it folded in. Only this
   * one is re-priced as the couple changes package or options.
   */
  const {
    data: activePrice,
    isFetching: isActivePriceFetching,
    isPreviousData: isActivePricePreviousData,
  } = useQuery(
    ['supplier-pricing-estimate-package', supplierId, year, guests, packageId, options],
    async () => {
      if (!supplierId || !year || typeof guests === 'undefined' || !packageId || !options.length)
        return null;

      const dataByYear = await Suppliers._.getById(supplierId).Prices.getAll();
      const yearData = dataByYear[year] ?? [];
      const selected = yearData.find((yearItem) => yearItem.id === packageId);

      if (!selected) return null;

      // Options are packages of the same year, so they come out of the data already fetched.
      return await Suppliers._.getById(supplierId)
        .Prices.getById(packageId)
        .getEstimate(
          { day: guests },
          selected,
          yearData.filter((yearItem) => options.includes(yearItem.id)),
        );
    },
    {
      cacheTime: SUPPLIER_QUERY_CACHE_TIME,
      staleTime: SUPPLIER_QUERY_CACHE_TIME,
      refetchOnWindowFocus: false,
      keepPreviousData: true,
      enabled: isEnabled && !!packageId && options.length > 0,
    },
  );

  const hasOptions = !!packageId && options.length > 0;

  /**
   * True while an estimate is on its way, including when the previous key's data is still
   * on screen, so callers can tell the prices don't yet match what is selected.
   */
  const isEstimateFetching =
    !isEnabled ||
    isFetching ||
    isPreviousData ||
    (hasOptions && (isActivePriceFetching || isActivePricePreviousData));

  return {
    data: data || [],
    activePrice,
    isLoading,
    // Option labels read this list, independently of the selected-package query.
    isPriceListCurrent: isEnabled && data != null && !isPreviousData && !isFetching,
    isEstimateFetching,
    isEnabled,
  };
};

export const useSupplierQueryParams = (): ISupplierQueryParams | undefined => {
  const router = useRouter();
  const { query, isReady } = router;
  return useMemo<ISupplierQueryParams | undefined>(() => {
    if (!isReady) return undefined;
    const refSupplier = getReflinkParams(query) as TRefSupplier;

    const supplierQueryId = query.id as string;
    const slug = query.slug as string;
    const { supplierId, numbers } = decodePublicSupplierId(supplierQueryId);

    if (numbers.length === 0) {
      void router.push(UrlHelper.error);
    }

    if (query.viewedLocation) {
      refSupplier.viewedLocation = query.viewedLocation as TRefSupplier['viewedLocation'];
    }

    if (query.viewedSection) {
      refSupplier.viewedSection = query.viewedSection as string;
    }

    return {
      supplierQueryId,
      slug,
      refSupplier,
      supplierId,
    };
  }, [isReady, query, router]);
};

export const useSupplierPhotos = (params?: Pick<ISupplierQueryParams, 'supplierId'>) => {
  const { data, isLoading } = useQuery(
    ['supplier-photos', params],
    async () => {
      if (params === undefined) return undefined;

      return values(
        await Suppliers._.getById(params.supplierId)
          .Photos.query([orderBy('order')])
          .get(),
      );
    },
    {
      enabled: !!params?.supplierId,
      refetchOnWindowFocus: false,
      staleTime: SUPPLIER_QUERY_CACHE_TIME,
      cacheTime: SUPPLIER_QUERY_CACHE_TIME,
    },
  );
  return {
    data,
    isLoading,
  };
};

export const useSupplierByPublicId = (params?: Pick<ISupplierQueryParams, 'supplierQueryId'>) => {
  // We can't use useMarket here to acquire the locale, because initially it might be incorrect
  // (the way the hook works, it's not guaranteed to be correct until the effect runs).
  const { locale } = useSelector(getUserL10n) || {};

  const searchFields = useSelector(getSearchFields) as SearchResponseFields;
  const bodyParams: FetchEnrichedSupplierParams = {
    locale,
    weddingMonths: searchFields?.weddingMonths,
    weddingWeekDays: searchFields?.weddingWeekDays,
  };
  const publicId = params?.supplierQueryId ? extractPublicId(params.supplierQueryId) : undefined;

  const { data, isLoading } = useQuery(
    ['supplier-by-public-id', publicId, bodyParams],
    async () => {
      if (params === undefined || !publicId) return undefined;

      return await fetchSupplierPromise(publicId, bodyParams);
    },
    {
      enabled: !!publicId,
      refetchOnWindowFocus: false,
      staleTime: SUPPLIER_QUERY_CACHE_TIME,
      cacheTime: SUPPLIER_QUERY_CACHE_TIME,
    },
  );
  return {
    data,
    isLoading,
  };
};

export const useSupplierDataByPublicId = (
  params?: Pick<ISupplierQueryParams, 'supplierQueryId'>,
) => {
  // We can't use useMarket here to acquire the locale, because initially it might be incorrect
  // (the way the hook works, it's not guaranteed to be correct until the effect runs).
  const { locale } = useSelector(getUserL10n) || {};
  const publicId = params?.supplierQueryId ? extractPublicId(params.supplierQueryId) : undefined;
  const { data, isLoading } = useQuery(
    ['supplier-data-by-public-id', publicId, locale],
    async () => {
      if (params === undefined || !publicId) return undefined;

      return await fetchSupplierData(publicId, locale);

      // local workaround for not loading supplier data
      // return new Promise((resolve) => {
      //   setTimeout(() => {
      //     fetchSupplierData(publicId, locale).then((result) => resolve(result));
      //   }, 5000);
      // });
    },
    {
      enabled: !!publicId,
      refetchOnWindowFocus: false,
      staleTime: SUPPLIER_QUERY_CACHE_TIME,
      cacheTime: SUPPLIER_QUERY_CACHE_TIME,
    },
  );
  return {
    data,
    isLoading,
  };
};
